import * as React from "react"
import { useTranslation } from "react-i18next"
import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui"

/** Exit motion — keep in sync with the Tailwind `duration-150` on the section. */
export const PERSONAS_BANNER_EXIT_MS = 150

export type PersonasNewAvailableBannerMode = "setup" | "review" | "finish"

/** Finish runs over the Amber Path, so the label swap alone under-reports that work is in flight. */
function BusySpinner() {
  return (
    <Icon
      name="loader"
      color="inherit"
      size="xs"
      aria-hidden
      className="motion-safe:animate-spin motion-reduce:animate-none"
    />
  )
}

export interface PersonasNewAvailableBannerProps {
  /** Parent owns dismissal so it can key it by suggested persona ids. */
  open: boolean
  suggestedCount: number
  hasCompletedOnboarding: boolean
  /** Rejects postdate the last completion, so someone still owes a Finish. */
  awaitingFinish: boolean
  onDismiss: () => void
  onSetup: () => void
  onReview: () => void
  onFinish: () => void
  canStart: boolean
  canFinish: boolean
  isFinishing?: boolean
  className?: string
}

/**
 * Resolves which of the three banner modes applies, or null when nothing is pending.
 * A finish that is still owed only surfaces once the suggestion queue is empty —
 * otherwise the review CTA is the more useful next step.
 */
export function resolvePersonasBannerMode(input: {
  suggestedCount: number
  hasCompletedOnboarding: boolean
  awaitingFinish: boolean
}): PersonasNewAvailableBannerMode | null {
  if (input.suggestedCount > 0) return input.hasCompletedOnboarding ? "review" : "setup"
  if (input.awaitingFinish) return "finish"
  return null
}

/** Standing banner above the library body pointing at whatever review work is outstanding. */
export function PersonasNewAvailableBanner({
  open,
  suggestedCount,
  hasCompletedOnboarding,
  awaitingFinish,
  onDismiss,
  onSetup,
  onReview,
  onFinish,
  canStart,
  canFinish,
  isFinishing = false,
  className,
}: PersonasNewAvailableBannerProps) {
  const { t } = useTranslation()
  const mode = resolvePersonasBannerMode({ suggestedCount, hasCompletedOnboarding, awaitingFinish })

  // Dismissal removes ~100px from the top of the page. Committing it straight away
  // snaps the table up under the reader's eyes, so the exit plays first and `onDismiss`
  // lands once the banner is already invisible. Mirrors `VersionUpdateBanner`.
  const [isExiting, setIsExiting] = React.useState(false)
  const exitTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(
    () => () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current)
    },
    [],
  )

  const handleDismiss = React.useCallback(() => {
    if (isExiting) return
    setIsExiting(true)
    exitTimerRef.current = setTimeout(() => {
      exitTimerRef.current = null
      onDismiss()
    }, PERSONAS_BANNER_EXIT_MS)
  }, [isExiting, onDismiss])

  // Finish is an obligation, not an announcement — it stays until it is done.
  // Keyed on `awaitingFinish` rather than on the mode: New is now a permanent
  // resting state (AD-2518), so a tenant with skipped personas sits in `review`
  // mode forever and would otherwise be able to dismiss an owed Finish away.
  const isDismissible = !awaitingFinish
  if (mode === null) return null
  if (isDismissible && !open) return null

  const actionAllowed = mode === "finish" ? canFinish : canStart
  const isPrimaryFinishing = mode === "finish" && isFinishing
  const title =
    mode === "finish"
      ? t("personas.newBanner.finishTitle", "Finish setting up your personas")
      : mode === "review"
        ? t("personas.newBanner.reviewTitle", {
            defaultValue: "{{count}} personas are waiting for your decision",
            count: suggestedCount,
          })
        : t("personas.newBanner.setupTitle", {
            defaultValue: "{{count}} new personas",
            count: suggestedCount,
          })
  const description =
    mode === "finish"
      ? t(
          "personas.newBanner.finishDescription",
          "Your rejected personas haven't been sent for recalculation yet. Finish setup to apply them.",
        )
      : mode === "review"
        ? t(
            "personas.newBanner.reviewDescription",
            "Adge found more customer types in your ads. They stay marked New until you approve or reject them.",
          )
        : t(
            "personas.newBanner.setupDescription",
            "Adge clustered your creatives into persona suggestions. Run setup to approve the ones that describe your customers.",
          )

  return (
    <section
      data-testid="personas-new-available-banner"
      data-mode={mode}
      aria-label={title}
      className={cn(
        // Diluted `primary-subtle` — the New chip uses the same tint at full strength, so the
        // chip reads as the loud instance and this as the quiet field of the same family.
        // AD-2518 made New a permanent resting state, so this is standing furniture rather
        // than an announcement: a full-strength alert surface here would never stop shouting.
        "flex flex-wrap items-start gap-3 rounded-lg border border-primary-border/40 bg-primary-subtle/50 px-4 py-3 sm:items-center sm:gap-4",
        // Exits accelerate away (`ease-in`); entrances decelerate in (`ease-out`).
        isExiting
          ? "motion-safe:animate-out motion-safe:fade-out motion-safe:slide-out-to-top-1 motion-safe:duration-150 motion-safe:ease-in motion-reduce:animate-none"
          : "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150 motion-safe:ease-out motion-reduce:animate-none",
        className,
      )}
    >
      <div className="min-w-[14rem] flex-1">
        <p className="text-sm font-semibold leading-snug tracking-[-0.01em] text-foreground">
          {title}
        </p>
        <p className="mt-0.5 max-w-[75ch] text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>

      <TooltipProvider delayDuration={300}>
        <div className="flex w-full shrink-0 flex-wrap items-center gap-2 self-stretch sm:w-auto sm:self-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex w-full sm:w-auto">
                <Button
                  type="button"
                  size="sm"
                  iconEnd={isPrimaryFinishing ? undefined : "arrow-right"}
                  disabled={!actionAllowed || isPrimaryFinishing || isExiting}
                  aria-busy={isPrimaryFinishing}
                  onClick={
                    mode === "finish" ? onFinish : mode === "review" ? onReview : onSetup
                  }
                  className="min-h-11 w-full sm:min-h-8 sm:w-auto"
                >
                  {isPrimaryFinishing ? <BusySpinner /> : null}
                  {mode === "finish"
                    ? isFinishing
                      ? t("personas.onboarding.finishReady.finishing", "Finishing…")
                      : t("personas.newBanner.finishCta", "Finish setup")
                    : mode === "review"
                      ? t("personas.newBanner.reviewCta", "Review")
                      : t("personas.newBanner.setupCta", "Setup")}
                </Button>
              </span>
            </TooltipTrigger>
            {!actionAllowed && (
              <TooltipContent>
                {t("common.noPermission", "You don't have permission to do this")}
              </TooltipContent>
            )}
          </Tooltip>

          {/* Review wins the primary slot, but an owed Finish must still be
              reachable from the banner rather than only from inside the carousel. */}
          {mode !== "finish" && awaitingFinish ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canFinish || isFinishing || isExiting}
              aria-busy={isFinishing}
              onClick={onFinish}
              className="min-h-11 w-full sm:min-h-8 sm:w-auto"
            >
              {isFinishing ? <BusySpinner /> : null}
              {isFinishing
                ? t("personas.onboarding.finishReady.finishing", "Finishing…")
                : t("personas.newBanner.finishCta", "Finish setup")}
            </Button>
          ) : null}

          {isDismissible ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-lg"
              iconStart="x"
              onClick={handleDismiss}
              disabled={isExiting}
              aria-label={t("personas.newBanner.dismissAria", "Dismiss banner")}
              className="min-h-11 min-w-11 shrink-0 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground [@media(hover:hover)_and_(pointer:fine)]:size-8"
            />
          ) : null}
        </div>
      </TooltipProvider>
    </section>
  )
}
